/**
 * useIrisDevice.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared hook for the /ws/device WebSocket connection.
 *
 * PROBLEMS this solves:
 *
 * 1. DUPLICATE CONNECTIONS ON TAB FOCUS
 *    Previously every component called callWebsocketIris() inside a
 *    visibilitychange handler WITHOUT checking if a connection already
 *    existed. Every tab focus created a brand new WebSocket, leaving
 *    the old one open and orphaned. You can see this in DevTools as
 *    multiple stacked "device" connections (your screenshot).
 *
 * 2. THREE COMPONENTS, THREE INDEPENDENT CONNECTIONS
 *    BookingsPage, FolderSearch, and ConfigHome each maintained their
 *    own wsRef and irisDeviceConnected state. Three components on screen
 *    = 3 simultaneous /ws/device connections, tripling load on the
 *    Java backend for no benefit.
 *
 * 3. STALE CONNECTION NOT CLEANED UP ON NAVIGATION
 *    wsRef.current was overwritten on each callWebsocketIris() call
 *    without calling .close() on the previous instance first, so the
 *    old TCP connection stayed open until the browser garbage-collected it.
 *
 * HOW THIS FIX WORKS:
 *
 * - A single WebSocket instance is shared across ALL components via a
 *   module-level singleton (not React state — module scope survives
 *   re-renders and component unmounts).
 *
 * - Components subscribe to status updates via a listener set.
 *   When the status changes, all subscribers are notified.
 *
 * - Before creating a new connection, readyState is checked.
 *   OPEN or CONNECTING → reuse, do NOT create a new one.
 *
 * - On visibilitychange → only reconnects if the socket is actually
 *   dead (CLOSED/null), not just because the tab came back into focus.
 *
 * - Connection persists for the lifetime of the tab, not tied to how many
 *   components are currently mounted. It is opened lazily by the first
 *   component that calls the hook and is only closed on tab/window unload.
 *   Earlier this was reference-counted and closed whenever the last
 *   subscriber unmounted (e.g. navigating to a page that doesn't use iris
 *   features). That reset deviceConnected to false, and the local IRIS
 *   service only pushes a status update on a device plug/unplug event —
 *   not on new client connection — so re-mounting on Bookings/FolderSearch
 *   left the Iris Search button stuck disabled even though the device was
 *   still connected, until the device was physically replugged.
 *
 * USAGE (replaces all callWebsocketIris / checkSerialDevices patterns):
 *
 *   import { useIrisDevice } from "@/hooks/useIrisDevice";
 *
 *   const { irisDeviceConnected, isIrisServiceRunning } = useIrisDevice();
 */

import { useEffect, useRef, useState } from "react";
import { WEB_SOCKET_URL } from "@/util/api";

const WS_URL = `${WEB_SOCKET_URL}/ws/device`;

// ─── Singleton state (module-level, shared across all hook instances) ────────

let socket = null; // The single WebSocket instance
let deviceConnected = false; // Last known device status
let serviceRunning = false; // True once the socket has connected at least once
const listeners = new Set(); // All active setState callbacks

/** Notify every mounted subscriber of the latest status. */
function broadcast() {
  listeners.forEach((fn) => fn({ deviceConnected, serviceRunning }));
}

/** Create a new WebSocket only if one isn't already alive. */
function connect() {
  // Guard: if already OPEN or CONNECTING, do nothing

  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING ||
      socket.readyState === WebSocket.CLOSING)
  ) {
    return;
  }

  // Clean up any dead socket reference before creating a new one
  if (socket) {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;
    socket = null;
  }

  socket = new WebSocket(WS_URL);

  socket.onopen = () => {
    serviceRunning = true;
    broadcast();
  };

  socket.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      deviceConnected = !!data?.connected;
      broadcast();
    } catch (e) {
      console.error("[useIrisDevice] Failed to parse message:", e);
    }
  };

  socket.onclose = () => {
    deviceConnected = false;
    // Keep serviceRunning as-is — the service was running, socket just closed
    broadcast();
    socket = null;
  };

  socket.onerror = (err) => {
    console.error("[useIrisDevice] WebSocket error:", err);
    serviceRunning = false;
    deviceConnected = false;
    broadcast();
  };
}

// ─── The Hook ────────────────────────────────────────────────────────────────

/**
 * Returns live iris device status. Manages the shared WebSocket internally.
 *
 * @returns {{ irisDeviceConnected: boolean, isIrisServiceRunning: boolean }}
 */
export function useIrisDevice() {
  const [status, setStatus] = useState({
    deviceConnected,
    serviceRunning,
  });

  // Keep a stable ref to this component's setState so we can remove it on unmount
  const setStatusRef = useRef(setStatus);
  setStatusRef.current = setStatus;

  useEffect(() => {
    // Register this component as a subscriber
    const listener = (newStatus) => setStatusRef.current(newStatus);
    listeners.add(listener);

    // Connect (or reuse existing connection)
    connect();

    // visibilitychange: reconnect ONLY if the socket is actually dead
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Only reconnect if socket is gone — NOT on every focus
        if (!socket || socket.readyState === WebSocket.CLOSED) {
          connect();
        }
      } else {
        // Tab hidden: just resync this subscriber's state, don't close the socket
        setStatusRef.current({ deviceConnected, serviceRunning });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Send current known status immediately to this new subscriber
    setStatusRef.current({ deviceConnected, serviceRunning });

    return () => {
      // Unregister this subscriber. The shared socket is intentionally left
      // open — it persists for the tab's lifetime so device status survives
      // navigation to pages that don't use the hook (see file header).
      listeners.delete(listener);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []); // empty deps — runs once per component mount/unmount

  return {
    irisDeviceConnected: status.deviceConnected,
    isIrisServiceRunning: status.serviceRunning,
  };
}
